/* @jsx h */
import { defineComponent, h, computed, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { formatValue, formatCompact } from "../../DataRoom/format";
import type {
  RevenueComparison as RevenueComparisonData,
  RevenueComparisonPoint,
} from "@app/api/trpc/routers/overview-charts";

/**
 * Revenue for the selected period, with the equivalent preceding period behind
 * it — "this week vs last week".
 *
 * WHY BOTH LINES SHARE ONE AXIS
 *   Same measure, same unit, so a second scale would be a lie about the
 *   comparison. Two y-scales can make a worse week look like a better one
 *   purely by choosing the ranges.
 *
 * WHY X IS DAY INDEX AND NOT DATE
 *   The periods occupy disjoint date ranges. Plotted against real dates they
 *   would sit side by side as two separate runs rather than overlaying, which
 *   is not a comparison at all. Both series run against position-within-period,
 *   which the SQL also weekday-aligns for the common Mon–Sun case. The real
 *   date lives in the tooltip and the axis tick.
 *
 * The current period is a solid, saturated line; the previous is muted and
 * dashed. Dash pattern is the secondary encoding — the two series stay
 * distinguishable in greyscale, in print, and under forced colors, where a hue
 * difference alone would not survive.
 */

export type RevenueComparisonProps = {
  data: RevenueComparisonData | null;
  currency: string;
  loading?: boolean;
};

/**
 * Slots 1 and 2 of the validated categorical palette.
 *
 * The previous period is deliberately NOT a washed-out blue. Every muted
 * blue-family step tried failed the validator twice over — chroma below the
 * "reads gray" floor, and ΔE ~10 against the current line, which is under the
 * 15 normal-vision floor. A greyed comparison line is a line nobody can see.
 * Two distinct hues clear every check in both themes with ΔE 28+ instead, and
 * "recessive" is carried by the dash pattern and the thinner stroke — which
 * also survives greyscale printing and forced-colors mode, where a hue
 * difference alone would not.
 */
const CURRENT_COLOR = "#2f7ded";
const PREVIOUS_COLOR = "#e2622c";

export const RevenueComparison = defineComponent<RevenueComparisonProps>({
  name: "RevenueComparison",
  props: ["data", "currency", "loading"] as any,
  setup(props) {
    const { t } = useLocalization();
    const hoverIndex = ref<number | null>(null);

    const split = computed(() => {
      const rows: RevenueComparisonPoint[] = props.data?.series ?? [];
      const current = rows
        .filter((r) => r.period === "current")
        .sort((a, b) => a.day_index - b.day_index);
      const previous = rows
        .filter((r) => r.period === "previous")
        .sort((a, b) => a.day_index - b.day_index);
      return { current, previous };
    });

    const maxValue = computed(() => {
      const all = [...split.value.current, ...split.value.previous].map(
        (r) => Number(r.gross_revenue) || 0,
      );
      const m = Math.max(0, ...all);
      // A flat-zero period must still render a sane axis rather than dividing
      // every point by zero.
      return m > 0 ? m : 1;
    });

    const totals = computed(() => {
      const cur = Number(props.data?.totals?.current?.gross_revenue) || 0;
      const prev = Number(props.data?.totals?.previous?.gross_revenue) || 0;
      // No baseline means no percentage — "+100%" off zero is noise, not signal.
      const delta = prev > 0 ? ((cur - prev) / prev) * 100 : null;
      return { cur, prev, delta };
    });

    /** Point coordinates in a 0..100 × 0..100 space, y inverted for SVG. */
    const toPoints = (rows: RevenueComparisonPoint[]) => {
      const n = rows.length;
      if (n === 0) return [];
      return rows.map((r, i) => ({
        row: r,
        // A single-point period sits mid-plot instead of dividing by zero.
        x: n === 1 ? 50 : (i / (n - 1)) * 100,
        y: 100 - ((Number(r.gross_revenue) || 0) / maxValue.value) * 100,
      }));
    };

    const currentPoints = computed(() => toPoints(split.value.current));
    const previousPoints = computed(() => toPoints(split.value.previous));

    const polyline = (pts: Array<{ x: number; y: number }>) =>
      pts.map((p) => `${p.x.toFixed(3)},${p.y.toFixed(3)}`).join(" ");

    /**
     * Which x-axis ticks to draw.
     *
     * Every day labelled is unreadable on a phone, so thin to at most ~4 on
     * narrow plots and ~7 otherwise. This is the "selective labels" rule: the
     * shape carries the story, the ticks orient it.
     */
    const tickIndices = computed(() => {
      const n = split.value.current.length;
      if (n <= 1) return [0];
      const target = n <= 7 ? n : 7;
      const step = Math.max(1, Math.round((n - 1) / (target - 1)));
      const out: number[] = [];
      for (let i = 0; i < n; i += step) out.push(i);
      if (out[out.length - 1] !== n - 1) out.push(n - 1);
      return out;
    });

    const gridLines = [0, 0.25, 0.5, 0.75, 1];

    return () => {
      if (props.loading) {
        return (
          <div class="card transition-shadow border rounded-lg md:rounded-box">
            <div class="card-body p-4 md:p-6 flex h-[300px] items-center justify-center">
              <span class="loading loading-spinner loading-md text-primary/70" />
            </div>
          </div>
        );
      }

      const cur = split.value.current;
      if (!props.data || cur.length === 0) {
        return (
          <div class="card transition-shadow border rounded-lg md:rounded-box">
            <div class="card-body p-4 md:p-6">
              <h3 class="card-title flex items-center gap-2 text-accent font-normal">
                <span class="material-symbols-outlined">trending_up</span>
                {t("reports.overview.comparison.title")}
              </h3>
              <p class="mt-6 text-center text-sm text-base-content/60">
                {t("reports.overview.comparison.empty")}
              </p>
            </div>
          </div>
        );
      }

      const hovered = hoverIndex.value;
      const hoveredCurrent = hovered != null ? cur[hovered] : null;
      const hoveredPrevious =
        hovered != null ? split.value.previous[hovered] : null;
      const delta = totals.value.delta;

      return (
        <div class="card transition-shadow border rounded-lg md:rounded-box">
          <div class="card-body p-4 md:p-6">
            <header class="mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
              <div class="min-w-0">
                <h3 class="card-title flex items-center gap-2 text-accent font-normal">
                  <span class="material-symbols-outlined">trending_up</span>
                  {t("reports.overview.comparison.title")}
                </h3>
                <p class="mt-0.5 text-xs text-base-content/60">
                  {t("reports.overview.comparison.description")}
                </p>
              </div>
              <div class="flex shrink-0 items-baseline gap-2">
                <span class="text-lg font-semibold  text-accent">
                  {formatValue(totals.value.cur, "money", props.currency)}
                </span>
                {delta != null ? (
                  <span
                    class={[
                      "rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
                      delta >= 0
                        ? "bg-success/15 text-success"
                        : "bg-error/15 text-error",
                    ].join(" ")}
                    title={t("reports.overview.comparison.delta_hint")}
                  >
                    {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%
                  </span>
                ) : null}
              </div>
            </header>

            {/*
            Legend is always present for two series — identity must never rest
            on colour alone. Each entry shows its own line style, so the dash
            pattern is legible here too, not just in the plot.
          */}
            <div class="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
              <span class="inline-flex items-center gap-1.5">
                <svg width="18" height="8" aria-hidden="true">
                  <line
                    x1="0"
                    y1="4"
                    x2="18"
                    y2="4"
                    stroke={CURRENT_COLOR}
                    stroke-width="2"
                  />
                </svg>
                <span class="text-base-content">
                  {t("reports.overview.comparison.current")}
                </span>
                <span class="text-base-content/55">
                  {props.data.current_from} → {props.data.current_to}
                </span>
              </span>
              <span class="inline-flex items-center gap-1.5">
                <svg width="18" height="8" aria-hidden="true">
                  <line
                    x1="0"
                    y1="4"
                    x2="18"
                    y2="4"
                    stroke={PREVIOUS_COLOR}
                    stroke-width="2"
                    stroke-dasharray="4 3"
                  />
                </svg>
                <span class="text-base-content">
                  {t("reports.overview.comparison.previous")}
                </span>
                <span class="text-base-content/55">
                  {props.data.previous_from} → {props.data.previous_to}
                </span>
              </span>
            </div>

            <div class="relative">
              {/* Value axis. Fixed left gutter; the plot is offset by the same
                amount so the overlay and the SVG share one coordinate box. */}
              {/*
              Ticks are absolutely positioned at their own percentage rather
              than flowed with justify-between. Flowed, the bottom label centres
              on the very bottom edge of the box and collides with the weekday
              row underneath; positioned, each one sits exactly on its gridline.
              The gutter is w-16 so "CHF 168.97" fits on one line — at w-12 it
              wrapped and dragged the label off its line.
            */}
              <div class="pointer-events-none absolute inset-y-0 left-0 w-16">
                {gridLines.map((g) => (
                  <span
                    key={g}
                    class="absolute right-2 -translate-y-1/2 whitespace-nowrap text-right text-[10px] tabular-nums text-base-content/70"
                    style={{ top: `${(1 - g) * 100}%` }}
                  >
                    {formatCompact(maxValue.value * g, "money", props.currency)}
                  </span>
                ))}
              </div>

              <div class="relative ml-16 h-[180px] sm:h-[220px]">
                {gridLines.map((g) => (
                  <div
                    key={g}
                    class="pointer-events-none absolute inset-x-0 border-t border-base-300/70"
                    style={{ top: `${(1 - g) * 100}%` }}
                  />
                ))}

                <svg
                  class="absolute inset-0 h-full w-full overflow-visible"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  role="img"
                  aria-label={t("reports.overview.comparison.title")}
                >
                  {/* Previous first, so the current period draws on top. */}
                  <polyline
                    points={polyline(previousPoints.value)}
                    fill="none"
                    stroke={PREVIOUS_COLOR}
                    // Thinner and dashed: recession comes from weight and
                    // pattern, not from draining the colour out of it.
                    stroke-width="1.5"
                    stroke-dasharray="4 3"
                    opacity="0.85"
                    vector-effect="non-scaling-stroke"
                    stroke-linejoin="round"
                    stroke-linecap="round"
                  />
                  <polyline
                    points={polyline(currentPoints.value)}
                    fill="none"
                    stroke={CURRENT_COLOR}
                    stroke-width="2"
                    vector-effect="non-scaling-stroke"
                    stroke-linejoin="round"
                    stroke-linecap="round"
                  />
                </svg>

                {/*
                Markers are HTML, not SVG circles: the viewBox uses
                preserveAspectRatio="none" so the plot stretches to the
                container, which would squash an SVG circle into an ellipse.
              */}
                {hovered != null && currentPoints.value[hovered] ? (
                  <div
                    class="pointer-events-none absolute z-10 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-base-100"
                    style={{
                      left: `${currentPoints.value[hovered].x}%`,
                      top: `${currentPoints.value[hovered].y}%`,
                      background: CURRENT_COLOR,
                    }}
                  />
                ) : null}
                {hovered != null && previousPoints.value[hovered] ? (
                  <div
                    class="pointer-events-none absolute z-10 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-base-100"
                    style={{
                      left: `${previousPoints.value[hovered].x}%`,
                      top: `${previousPoints.value[hovered].y}%`,
                      background: PREVIOUS_COLOR,
                    }}
                  />
                ) : null}

                {/* Crosshair + hit targets. One full-height column per index, so
                  the hit area is far bigger than the mark itself — this is what
                  makes the chart usable with a thumb. */}
                <div class="absolute inset-0 flex">
                  {cur.map((row, i) => (
                    <button
                      key={row.day}
                      type="button"
                      class="group h-full flex-1 cursor-default border-0 bg-transparent p-0"
                      onMouseenter={() => (hoverIndex.value = i)}
                      onMouseleave={() => (hoverIndex.value = null)}
                      onFocus={() => (hoverIndex.value = i)}
                      onBlur={() => (hoverIndex.value = null)}
                      aria-label={`${row.day}: ${formatValue(row.gross_revenue, "money", props.currency)}`}
                    >
                      <span
                        class={[
                          "pointer-events-none block h-full w-px",
                          hovered === i
                            ? "bg-base-content/25"
                            : "bg-transparent",
                        ].join(" ")}
                        style={{ marginLeft: "50%" }}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* X ticks, thinned. */}
              <div class="relative ml-16 mt-2.5 h-4">
                {tickIndices.value.map((i) => {
                  const p = currentPoints.value[i];
                  if (!p) return null;
                  return (
                    <span
                      key={i}
                      class="absolute -translate-x-1/2 whitespace-nowrap text-[10px] text-base-content/55"
                      style={{ left: `${p.x}%` }}
                    >
                      {cur[i].weekday}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Tooltip as a stable readout row rather than a floating box: on a
              phone a floating tooltip sits under the thumb. Reserved height so
              the chart does not jump when it appears. */}
            <div class="mt-3 min-h-[2.75rem] rounded-lg border border-base-300 bg-base-200/60 px-3 py-2 text-xs">
              {hoveredCurrent ? (
                <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span class="inline-flex items-center gap-1.5">
                    <span
                      class="inline-block h-2 w-2 rounded-full"
                      style={{ background: CURRENT_COLOR }}
                    />
                    <span class="text-base-content/70">
                      {hoveredCurrent.day}
                    </span>
                    <span class="font-mono font-semibold tabular-nums text-base-content">
                      {formatValue(
                        hoveredCurrent.gross_revenue,
                        "money",
                        props.currency,
                      )}
                    </span>
                  </span>
                  {hoveredPrevious ? (
                    <span class="inline-flex items-center gap-1.5">
                      <span
                        class="inline-block h-2 w-2 rounded-full"
                        style={{ background: PREVIOUS_COLOR }}
                      />
                      <span class="text-base-content/70">
                        {hoveredPrevious.day}
                      </span>
                      <span class="font-mono tabular-nums text-base-content/80">
                        {formatValue(
                          hoveredPrevious.gross_revenue,
                          "money",
                          props.currency,
                        )}
                      </span>
                    </span>
                  ) : null}
                </div>
              ) : (
                <span class="text-base-content/50">
                  {t("reports.overview.comparison.hover_hint")}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    };
  },
});
